// MARK://dhk8/pos/components/RefundDialog
// F3 sprint — order-level refund dialog. Distinct from the legacy payment-level
// refund flow inside ReceiptPage (which calls refundOrderPayment per payment row);
// this dialog targets the new POST /v1/orders/:orderId/refund endpoint and asks
// for amount + reason + manager PIN. The PIN is verified out-of-band via the
// shared `requestManagerPin('ticket.refund')` gate, then refundOrder() is called
// with `managerPinPasswordHash` as proof.
//
// Usage:
//   <RefundDialog open={open} order={order} currentUserId={userId}
//     onClose={() => setOpen(false)} onRefunded={(r) => …} />

import { useEffect, useState } from 'react'
import { RotateCcw } from 'lucide-react'

import { refundOrder, type RefundOrderResult } from '../lib/clients/orders.js'
import { requireManagerAuth } from '../features/security/pin-gate-store.js'
import { formatCents } from '../lib/money.js'

/** Minimal order shape we need — accepts both `BackendOrderDto` from the client
 *  and the UI `OrderDto` from order-store (both expose id/folio/total/paid). */
export interface RefundDialogOrder {
  readonly id: string
  readonly folio: string
  readonly totalCents: number
  readonly paidCents?: number | null
}

export interface RefundDialogProps {
  readonly open: boolean
  readonly order: RefundDialogOrder
  /** User performing the action (recorded as `managerUserId` on the PosRefund). */
  readonly currentUserId: string
  readonly onClose: () => void
  readonly onRefunded?: (result: RefundOrderResult) => void
}

export default function RefundDialog({ open, order, currentUserId, onClose, onRefunded }: RefundDialogProps) {
  // Default amount = total of the order (full refund). User may reduce for partial.
  const [amountInput, setAmountInput] = useState<string>(() => (order.totalCents / 100).toFixed(2))
  const [reason, setReason] = useState<string>('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setAmountInput((order.totalCents / 100).toFixed(2))
      setReason('')
      setError(null)
      setBusy(false)
    }
  }, [open, order.totalCents])

  if (!open) return null

  const amountCents = Math.round(Number(amountInput) * 100) || 0
  const validAmount = amountCents > 0 && amountCents <= (order.paidCents ?? 0)
  const canSubmit = validAmount && reason.trim().length > 0 && !busy

  async function submit() {
    if (!canSubmit) return
    setBusy(true); setError(null)
    try {
      const auth = await requireManagerAuth('ticket.refund')
      if (!auth.proceed) { setBusy(false); return }
      const result = await refundOrder(order.id, {
        amountCents, reason: reason.trim(),
        managerUserId: currentUserId,
        // TCK-0016: el canal `managerPinPasswordHash` ahora porta el TOKEN de
        // autorización de gerente (verificable server-side). Para gerencia/owner
        // (proceed directo sin PIN) va '' y el backend lo deja pasar por
        // capability === true; para cajero va el token del PIN.
        managerPinPasswordHash: auth.managerAuthToken ?? '',
      })
      onRefunded?.(result)
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo procesar el reembolso')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      className="dhk8-refund-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={`Reembolsar orden ${order.folio}`}
      onClick={() => !busy && onClose()}
    >
      <div className="dhk8-refund" onClick={(e) => e.stopPropagation()}>
        <div className="dhk8-refund__title">
          <RotateCcw size={16} aria-hidden /> Reembolsar orden {order.folio}
        </div>
        <p className="dhk8-refund__hint">
          Total pagado: <strong>{formatCents((order.paidCents ?? 0))}</strong>. Indica monto, motivo y
          autoriza con PIN de gerente. Se asienta un registro de reembolso vinculado a la orden.
        </p>

        <label className="dhk8-refund__field">
          <span>Monto a reembolsar (MXN)</span>
          <input
            type="number"
            inputMode="decimal"
            step="0.01"
            min="0"
            value={amountInput}
            onChange={(e) => setAmountInput(e.currentTarget.value)}
            disabled={busy}
          />
          {!validAmount && amountInput && (
            <span className="dhk8-refund__field-err">
              {amountCents > (order.paidCents ?? 0)
                ? `Excede el total pagado (${formatCents((order.paidCents ?? 0))})`
                : 'Monto inválido'}
            </span>
          )}
        </label>

        <label className="dhk8-refund__field">
          <span>Motivo</span>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.currentTarget.value)}
            placeholder="p. ej. cobro duplicado, error de captura"
            disabled={busy}
            autoFocus
          />
        </label>

        {error && <div className="dhk8-refund__err">{error}</div>}

        <div className="dhk8-refund__actions">
          <button type="button" className="dhk8-refund-btn" disabled={busy} onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="dhk8-refund-btn dhk8-refund-btn--danger"
            disabled={!canSubmit}
            onClick={() => void submit()}
          >
            {busy ? 'Procesando…' : `Reembolsar ${formatCents(amountCents)}`}
          </button>
        </div>
      </div>
      <style>{styles}</style>
    </div>
  )
}

const styles = `
  .dhk8-refund-overlay { position:fixed; inset:0; z-index:var(--pos-z-sheet, 200); display:flex; align-items:center; justify-content:center; padding:var(--pos-space-4,16px); background:rgba(0,0,0,.5); }
  .dhk8-refund { width:100%; max-width:380px; display:flex; flex-direction:column; gap:var(--pos-space-3,12px); padding:var(--pos-space-5,20px); background:var(--pos-surface,#161C18); border:1px solid var(--pos-separator,rgba(255,255,255,.07)); border-radius:var(--pos-radius-xl,16px); box-shadow:var(--pos-shadow-3,0 16px 48px -12px rgba(0,0,0,.5)); }
  .dhk8-refund__title { display:flex; align-items:center; gap:var(--pos-space-2,8px); font-family:var(--font-display,system-ui); font-size:var(--pos-text-lg,17px); font-weight:var(--pos-weight-semibold,600); color:var(--pos-fg,#E8EFE9); }
  .dhk8-refund__hint { margin:0; font-size:var(--pos-text-xs,12px); color:var(--pos-fg-tertiary,#7E8C82); line-height:1.4; }
  .dhk8-refund__hint strong { color:var(--pos-fg,#E8EFE9); font-weight:var(--pos-weight-semibold,600); }
  .dhk8-refund__field { display:flex; flex-direction:column; gap:4px; font-size:var(--pos-text-xxs,11px); letter-spacing:.08em; text-transform:uppercase; color:var(--pos-fg-tertiary,#7E8C82); }
  .dhk8-refund__field input { font:inherit; font-size:var(--pos-text-md,15px); text-transform:none; letter-spacing:normal; min-height:var(--pos-touch-target,44px); padding:0 var(--pos-space-3,12px); border:1px solid var(--pos-separator-strong,rgba(255,255,255,.14)); border-radius:var(--pos-radius-md,8px); background:var(--pos-canvas,#0D110F); color:var(--pos-fg,#E8EFE9); outline:none; transition:border-color var(--pos-duration-fast,120ms) ease, box-shadow var(--pos-duration-fast,120ms) ease; }
  .dhk8-refund__field input:focus { border-color:var(--pos-tint,#1F8B3A); box-shadow:0 0 0 3px var(--pos-tint-soft,rgba(31,139,58,.12)); }
  .dhk8-refund__field-err { font-size:var(--pos-text-xxs,11px); color:var(--pos-danger,#E5484D); text-transform:none; letter-spacing:normal; }
  .dhk8-refund__err { color:var(--pos-danger,#E5484D); background:var(--pos-danger-bg,rgba(229,72,77,.14)); border:1px solid color-mix(in oklch, var(--pos-danger,#E5484D) 40%, transparent); border-radius:var(--pos-radius-md,8px); padding:var(--pos-space-2,8px) var(--pos-space-3,12px); font-size:var(--pos-text-sm,13px); }
  .dhk8-refund__actions { display:flex; gap:var(--pos-space-2,8px); justify-content:flex-end; }
  .dhk8-refund-btn { display:inline-flex; align-items:center; gap:var(--pos-space-2,8px); padding:var(--pos-space-2,8px) var(--pos-space-4,16px); min-height:var(--pos-touch-target,44px); border:1px solid var(--pos-separator-strong,rgba(255,255,255,.14)); border-radius:var(--pos-radius-md,8px); background:transparent; color:var(--pos-fg-secondary,#B6BFB8); font-size:var(--pos-text-sm,13px); font-weight:var(--pos-weight-medium,500); cursor:pointer; }
  .dhk8-refund-btn:hover:not(:disabled) { background:var(--pos-elevated,#1E2520); color:var(--pos-fg,#E8EFE9); }
  .dhk8-refund-btn:disabled { opacity:.5; cursor:not-allowed; }
  .dhk8-refund-btn--danger { background:transparent; color:var(--pos-danger,#E5484D); border-color:color-mix(in oklch, var(--pos-danger,#E5484D) 45%, transparent); }
  .dhk8-refund-btn--danger:hover:not(:disabled) { background:var(--pos-danger-bg,rgba(229,72,77,.14)); color:var(--pos-danger,#E5484D); }
`
